import {
  ChangeDetectorRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  Directive
} from '@angular/core';
import {Observable} from 'rxjs';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FormFieldGroupConfig} from "../model/api/form-field-group-config";
import {CcBaseComponent} from "./cc-base.component";
import {CcFormDialogComponent} from "./form-dialog/cc-form-dialog.component";
import {BatchUpdateRequest} from "../model/api/batch-update-request";
import {ApiValidation} from "../model/api/api-validation";

/**
 * Shared base for batch update dialogs (Trade, Order, etc.)
 */
@Directive()
export abstract class BatchActionBaseFormComponent<T, TBatchConfigService extends {
  fieldsToUpdate: string[];
  loadConfig(component: any): FormFieldGroupConfig[];
}
> extends CcBaseComponent {

  formLoading = false;
  formSubmitted = true;
  formConfig: FormFieldGroupConfig[] = [];
  dialogFormTitle = '';

  @Input() formRecord!: T;
  @Input() transPartKey!: string;
  @Input() selectedRecords: T[] = [];
  @Input() dialogFormVisible = false;
  @Input() tracker: any;
  @Input() settingsFormLoadingProgressValue = 100;

  @Output() dialogFormVisibleChange = new EventEmitter<boolean>();
  @Output() onSavedForm = new EventEmitter<T>();

  @ViewChild('batchUpdateFormDialog')
  ccFormDialog!: CcFormDialogComponent;

  protected constructor(
    cdref: ChangeDetectorRef,
    messageService: MessageService,
    translateService: TranslateService,
    protected readonly confirmationService: ConfirmationService,
    protected readonly formConfigService: TBatchConfigService
  ) {
    super(cdref, messageService, translateService);
  }

  protected initConfigs(): void {
    super.initConfigs();
    this.formConfig = this.formConfigService.loadConfig(this);
  }

  closeDialogForm(): void {
    this.dialogFormVisible = false;
    this.dialogFormVisibleChange.emit(false);
    this.markForCheck();
  }

  save(): void {
    this.formLoading = true;

    const request = new BatchUpdateRequest<T>();
    request.recordToApply = {...this.formRecord};
    request.recordIds = this.selectedRecords.map((r: any) => r.id.toString());
    request.fieldToUpdate = [...this.formConfigService.fieldsToUpdate];

    this.getSaveAction(request).subscribe(validation => {
      if (validation.success) {
        this.formConfigService.fieldsToUpdate.splice(0);
        this.onSavedForm.emit(this.formRecord);
        this.closeDialogForm();
      } else {
        this.errorMessage(validation.message);
      }
      this.formLoading = false;
    });
  }

  dialogFormVisibleChanged(open: boolean): void {
    if (open) {
      this.formConfig = this.formConfigService.loadConfig(this);
    }

    this.dialogFormTitle = open
      ? this.getTranslation('common.table.batch_update_button')
      + ' ' + this.selectedRecords.length + ' '
      + this.getTranslation('page.' + this.transPartKey + '.item_name') + 's'
      : '';

    this.dialogFormVisibleChange.emit(open);
    this.formConfigService.fieldsToUpdate.splice(0);

    this.ccFormDialog.reloadFieldValueMap();
    setTimeout(() => {
      this.formLoading = !open;
      this.markForCheck();
    });
  }

  confirmDialog(
    action: () => void,
    titleKey: string,
    messageKey: string,
    submitKey = 'common.dialog.ok',
    cancelKey = 'common.dialog.cancel'
  ): void {
    this.confirmationService.confirm({
      header: this.getTranslation(titleKey),
      message: this.getTranslation(messageKey),
      icon: 'fas fa-exclamation-triangle',
      acceptLabel: this.getTranslation(submitKey),
      rejectLabel: this.getTranslation(cancelKey),
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      rejectButtonStyleClass: 'p-button-warning p-button-sm',
      accept: action
    });
  }

  protected abstract getSaveAction(
    request: BatchUpdateRequest<T>
  ): Observable<ApiValidation<any>>;
}
